import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { 
  Mail, Send, RefreshCw, Inbox, ChevronRight, X, 
  Paperclip, Clock, User, ArrowLeft, AlertCircle, CheckCircle, Loader, Settings
} from 'lucide-react';

/**
 * Mailbox Component - Displays client emails in ClientFiche
 * Reads and sends emails via IMAP/SMTP for the client's contact@xxx.store address
 */
export default function Mailbox({ clientId, emailAddress, emailPassword, emailServer, onEmailConfigured }) {
  const [emails, setEmails] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [showConfig, setShowConfig] = useState(!emailAddress);
  const [activeFolder, setActiveFolder] = useState('INBOX'); // INBOX ou INBOX.Sent

  const fetchEmails = useCallback(async (folder = activeFolder) => {
    if (!emailAddress) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await api.get(`/emails/client/${clientId}/inbox?folder=${encodeURIComponent(folder)}&limit=20`);
      setEmails(res.data.emails || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend returns structured error objects for IMAP issues
      if (detail && typeof detail === 'object') {
        setError(detail);
      } else {
        setError(typeof detail === 'string' ? detail : 'Erreur de connexion à la boîte mail');
      }
      console.error('Email fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [clientId, emailAddress, activeFolder]);

  useEffect(() => {
    if (emailAddress) {
      fetchEmails(activeFolder);
      setShowConfig(false);
    }
  }, [emailAddress, activeFolder]);

  const switchFolder = (folder) => {
    setActiveFolder(folder);
    setSelectedEmail(null);
    fetchEmails(folder);
  };

  const openEmail = async (uid) => {
    setLoadingEmail(true);
    try {
      const res = await api.get(`/emails/client/${clientId}/email/${uid}`);
      setSelectedEmail(res.data);
      // Refresh list to update read status
      await fetchEmails();
    } catch (e) {
      toast.error('Erreur lors de l\'ouverture de l\'email');
    } finally {
      setLoadingEmail(false);
    }
  };

  // Show configuration form if no email is set
  if (!emailAddress || showConfig) {
    return (
      <EmailConfigForm 
        clientId={clientId} 
        currentEmail={emailAddress}
        currentPassword={emailPassword}
        currentServer={emailServer}
        onConfigured={(email, password, server) => {
          setShowConfig(false);
          if (onEmailConfigured) {
            onEmailConfigured(email, password, server);
          }
        }}
        onCancel={emailAddress ? () => setShowConfig(false) : null}
      />
    );
  }

  if (error) {
    // Parse structured error from backend
    const errorData = typeof error === 'object' ? error : null;
    const errorMessage = errorData?.message || (typeof error === 'string' ? error : 'Erreur de connexion à la boîte mail');
    const suggestion = errorData?.suggestion;
    const availableFolders = errorData?.available_folders;

    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">Erreur de connexion</p>
            <p className="text-red-600 text-sm mt-1">{errorMessage}</p>
            {suggestion && (
              <p className="text-red-500 text-xs mt-1 italic">{suggestion}</p>
            )}
            {availableFolders && availableFolders.length > 0 && (
              <div className="mt-3 p-3 bg-white/60 rounded-lg border border-red-100">
                <p className="text-xs font-medium text-gray-700 mb-1">Dossiers disponibles sur ce compte :</p>
                <div className="flex flex-wrap gap-1">
                  {availableFolders.map((f, i) => (
                    <span key={`folder-${f}`} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-mono">{f}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <button 
                onClick={() => fetchEmails(activeFolder)}
                className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200 transition-colors"
              >
                <RefreshCw size={14} />
                Réessayer
              </button>
              <button 
                onClick={async () => {
                  try {
                    const res = await api.get(`/emails/client/${clientId}/folders`);
                    const folders = res.data.folders || [];
                    if (folders.length > 0) {
                      toast.success(`Dossiers trouvés : ${folders.join(', ')}`);
                    } else {
                      toast.error('Aucun dossier trouvé — le compte est peut-être désactivé');
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Impossible de lister les dossiers');
                  }
                }}
                className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm hover:bg-amber-200 transition-colors"
                data-testid="diagnose-email-btn"
              >
                <Settings size={14} />
                Diagnostiquer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[hsl(222,47%,11%)] to-[hsl(222,47%,20%)] text-white px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
              <Mail size={20} />
            </div>
            <div>
              <h3 className="font-semibold">Boîte Mail</h3>
              <p className="text-blue-200 text-xs">{emailAddress}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && activeFolder === 'INBOX' && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadCount} non lu{unreadCount > 1 ? 's' : ''}
              </span>
            )}
            <button
              onClick={() => setShowConfig(true)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Paramètres email"
            >
              <Settings size={16} />
            </button>
            <button
              onClick={() => fetchEmails(activeFolder)}
              disabled={loading}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="refresh-emails-btn"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => setShowCompose(true)}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-sm transition-colors"
              data-testid="compose-email-btn"
            >
              <Send size={14} />
              Nouveau
            </button>
          </div>
        </div>
      </div>

      {/* Folder Tabs */}
      <div className="flex border-b border-gray-200 bg-gray-50">
        <button
          onClick={() => switchFolder('INBOX')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
            activeFolder === 'INBOX'
              ? 'text-[hsl(222,47%,11%)] border-b-2 border-[hsl(222,47%,11%)] bg-white'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
          data-testid="folder-inbox-btn"
        >
          <Inbox size={16} />
          Reçus
          {unreadCount > 0 && activeFolder !== 'INBOX' && (
            <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{unreadCount}</span>
          )}
        </button>
        <button
          onClick={() => switchFolder('INBOX.Sent')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
            activeFolder === 'INBOX.Sent'
              ? 'text-[hsl(222,47%,11%)] border-b-2 border-[hsl(222,47%,11%)] bg-white'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
          data-testid="folder-sent-btn"
        >
          <Send size={16} />
          Envoyés
        </button>
      </div>

      {/* Content */}
      <div className="min-h-[300px] max-h-[500px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={24} className="animate-spin text-gray-400" />
          </div>
        ) : selectedEmail ? (
          <EmailView 
            email={selectedEmail} 
            onBack={() => setSelectedEmail(null)}
            clientId={clientId}
            fromAddress={emailAddress}
          />
        ) : emails.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Inbox size={40} className="mb-3 opacity-50" />
            <p className="text-sm">{activeFolder === 'INBOX' ? 'Aucun email reçu' : 'Aucun email envoyé'}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {emails.map((email) => (
              <EmailRow 
                key={email.uid} 
                email={email} 
                onClick={() => openEmail(email.uid)}
                loading={loadingEmail}
                isSent={activeFolder === 'INBOX.Sent'}
              />
            ))}
          </div>
        )}
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <ComposeEmail
          clientId={clientId}
          fromAddress={emailAddress}
          onClose={() => setShowCompose(false)}
          onSent={() => {
            setShowCompose(false);
            fetchEmails();
          }}
        />
      )}
    </div>
  );
}

function EmailRow({ email, onClick, loading, isSent = false }) {
  const isUnread = !email.is_read;
  const date = email.date ? new Date(email.date) : null;
  
  // Pour les emails envoyés, afficher le destinataire (to) au lieu de l'expéditeur (from)
  const displayContact = isSent 
    ? (email.to?.split('<')[0]?.trim() || email.to || 'Inconnu')
    : (email.from?.split('<')[0]?.trim() || 'Inconnu');
  
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-start gap-3 ${isUnread && !isSent ? 'bg-blue-50/50' : ''}`}
      data-testid={`email-row-${email.uid}`}
    >
      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isUnread && !isSent ? 'bg-blue-500' : 'bg-transparent'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={`text-sm truncate ${isUnread && !isSent ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
            {isSent && <span className="text-gray-400 mr-1">À:</span>}
            {displayContact}
          </p>
          {date && (
            <span className="text-xs text-gray-400 flex-shrink-0">
              {date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
            </span>
          )}
        </div>
        <p className={`text-sm truncate ${isUnread && !isSent ? 'text-gray-900' : 'text-gray-600'}`}>
          {email.subject || '(Sans objet)'}
        </p>
        <p className="text-xs text-gray-400 truncate mt-0.5">
          {email.body_text?.substring(0, 80) || ''}
        </p>
      </div>
      {email.has_attachments && (
        <Paperclip size={14} className="text-gray-400 flex-shrink-0 mt-1" />
      )}
      <ChevronRight size={16} className="text-gray-300 flex-shrink-0 mt-1" />
    </button>
  );
}

function EmailView({ email, onBack, clientId, fromAddress }) {
  const date = email.date ? new Date(email.date) : null;
  const [showReply, setShowReply] = useState(false);

  return (
    <div className="divide-y divide-gray-100">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-3"
        >
          <ArrowLeft size={16} />
          Retour à la liste
        </button>
        <h4 className="font-semibold text-gray-900 text-lg">{email.subject || '(Sans objet)'}</h4>
        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <User size={14} />
            {email.from}
          </span>
          {date && (
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {date.toLocaleString('fr-FR')}
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-4">
        {email.body_html ? (
          <div 
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.body_html) }}
          />
        ) : (
          <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
            {email.body_text || 'Contenu vide'}
          </pre>
        )}
      </div>

      {/* Attachments */}
      {email.attachments?.length > 0 && (
        <div className="px-4 py-3 bg-gray-50">
          <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
            <Paperclip size={12} />
            {email.attachments.length} pièce(s) jointe(s)
          </p>
          <div className="flex flex-wrap gap-2">
            {email.attachments.map((att, i) => (
              <span key={`att-${att.filename}-${i}`} className="px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-600">
                {att.filename}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-4 py-3 flex gap-2">
        <button
          onClick={() => setShowReply(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[hsl(222,47%,11%)] text-white rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] transition-colors"
        >
          <Send size={14} />
          Répondre
        </button>
      </div>

      {showReply && (
        <ComposeEmail
          clientId={clientId}
          fromAddress={fromAddress}
          replyTo={email.from?.match(/<(.+)>/)?.[1] || email.from}
          replySubject={`Re: ${email.subject}`}
          onClose={() => setShowReply(false)}
          onSent={() => setShowReply(false)}
        />
      )}
    </div>
  );
}

function ComposeEmail({ clientId, fromAddress, replyTo, replySubject, onClose, onSent }) {
  const [to, setTo] = useState(replyTo || '');
  const [subject, setSubject] = useState(replySubject || '');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!to || !subject || !body) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSending(true);
    try {
      const res = await api.post(`/emails/client/${clientId}/send`, {
        to,
        subject,
        body,
        html: false
      });

      if (res.data.success) {
        toast.success('Email envoyé !');
        onSent();
      } else {
        toast.error(res.data.error || 'Erreur lors de l\'envoi');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Nouveau message</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <div className="p-4 space-y-3">
          <div className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
            De: <span className="font-medium text-gray-700">{fromAddress}</span>
          </div>
          
          <input
            type="email"
            placeholder="À"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
            data-testid="email-to-input"
          />
          
          <input
            type="text"
            placeholder="Objet"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
            data-testid="email-subject-input"
          />
          
          <textarea
            placeholder="Votre message..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] resize-none"
            data-testid="email-body-input"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-white transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="flex items-center gap-2 px-4 py-2 bg-[hsl(222,47%,11%)] text-white rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] disabled:opacity-50 transition-colors"
            data-testid="send-email-btn"
          >
            {sending ? (
              <RefreshCw size={14} className="animate-spin" />
            ) : (
              <Send size={14} />
            )}
            {sending ? 'Envoi...' : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Email Configuration Form - Allows configuring email from client page
 */
function EmailConfigForm({ clientId, currentEmail, currentPassword, currentServer, onConfigured, onCancel }) {
  const [emailForm, setEmailForm] = useState({
    email: currentEmail || '',
    password: currentPassword || 'Azerty1234@',
    server: currentServer || 'ssl0.ovh.net'
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Auto-detect server based on email domain
  const detectServer = (email) => {
    if (!email || !email.includes('@')) return 'ssl0.ovh.net';
    const domain = email.split('@')[1].toLowerCase();
    
    if (domain.includes('gmail')) return 'imap.gmail.com';
    if (domain.includes('outlook') || domain.includes('hotmail')) return 'outlook.office365.com';
    if (domain.includes('yahoo')) return 'imap.mail.yahoo.com';
    return 'ssl0.ovh.net'; // Default OVH
  };

  // Test email connection
  const testConnection = async () => {
    if (!emailForm.email || !emailForm.password) {
      toast.error('Email et mot de passe requis');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const res = await api.post('/emails/test-connection', {
        email: emailForm.email,
        password: emailForm.password,
        imap_server: emailForm.server
      });
      
      if (res.data.success) {
        setTestResult({ success: true, message: 'Connexion réussie !' });
        
        // Save to client
        try {
          await api.put(`/clients/${clientId}`, {
            email_domaine: emailForm.email,
            email_domaine_password: emailForm.password,
            email_domaine_server: emailForm.server,
            email_domaine_status: 'active'
          });
          toast.success('Email configuré et sauvegardé !');
          
          if (onConfigured) {
            onConfigured(emailForm.email, emailForm.password, emailForm.server);
          }
        } catch (e) {
          toast.error('Erreur lors de la sauvegarde');
        }
      } else {
        setTestResult({ success: false, message: res.data.error || 'Échec de connexion' });
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Erreur de connexion';
      setTestResult({ success: false, message: errorMsg });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
            <Mail size={20} />
          </div>
          <div>
            <h3 className="font-semibold">Configurer la boîte mail</h3>
            <p className="text-blue-200 text-xs">Connectez un email existant à ce client</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Adresse email</label>
          <input
            type="email"
            value={emailForm.email}
            onChange={(e) => {
              setEmailForm({ 
                ...emailForm, 
                email: e.target.value,
                server: detectServer(e.target.value)
              });
              setTestResult(null);
            }}
            placeholder="contact@exemple.com"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="config-email-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
          <input
            type="password"
            value={emailForm.password}
            onChange={(e) => {
              setEmailForm({ ...emailForm, password: e.target.value });
              setTestResult(null);
            }}
            placeholder="••••••••"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="config-password-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Serveur IMAP</label>
          <input
            type="text"
            value={emailForm.server}
            onChange={(e) => {
              setEmailForm({ ...emailForm, server: e.target.value });
              setTestResult(null);
            }}
            placeholder="ssl0.ovh.net"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
            data-testid="config-server-input"
          />
          <p className="text-xs text-gray-500 mt-1">Auto-détecté selon le domaine de l'email</p>
        </div>

        {/* Test result */}
        {testResult && (
          <div className={`p-3 rounded-lg flex items-center gap-2 ${testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {testResult.success ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            <span className="text-sm font-medium">{testResult.message}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
          )}
          <button
            onClick={testConnection}
            disabled={testing || !emailForm.email || !emailForm.password}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            data-testid="config-test-btn"
          >
            {testing ? (
              <>
                <Loader size={16} className="animate-spin" />
                Test en cours...
              </>
            ) : (
              <>
                <RefreshCw size={16} />
                Tester et sauvegarder
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
